/**
 * The member's sign-in and pipeline sheet. Three moments share it:
 *
 *   1. The moment the committee ADDS a member — they are `pending` now, so it
 *      says where they went (Membership requests, not the roster) and offers
 *      the payment link or cash.
 *   2. The moment a membership is ACTIVATED — from Requests. The member is
 *      EMAILED a one-time link to set their own password (owner's direction,
 *      2 Oct 2026). The committee never generates, sees or passes on a
 *      password any more.
 *   3. "Sign-in details" on the member detail page — how they sign in, and a
 *      way to resend that email if it was lost or expired.
 *
 * After their first password, Forgot password on the sign-in screen is the
 * member's own way back in — no committee involvement.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClub, useFees } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { TIERS, bibNo, feeFor, formatMoney } from '../../data';
import { Button, FormError, Hint, SheetActions, SheetHead } from '../ui';
import { PayLinkSheet, RecordPaymentSheet } from './MoneySheets';

/** One copyable credential line. */
function CopyRow({ label, value }) {
  const { toast } = useUI();
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-b border-line py-2 last:border-0">
      <span className="font-market text-[10.5px] uppercase tracking-wide text-copy-label">
        {label}
      </span>
      <span className="flex items-center gap-2">
        <code className="font-market text-[12px] text-ink-700">{value}</code>
        <button
          type="button"
          className="rounded border border-line px-1.5 py-0.5 font-market text-[10px] text-copy-label transition hover:border-ink-700 hover:text-ink-700"
          onClick={() => {
            navigator.clipboard?.writeText(value);
            toast(`${label} copied`);
          }}
        >
          copy
        </button>
      </span>
    </div>
  );
}

// Props: { memberId } — resolved from club state, so this always renders the
// stored record rather than the draft that was typed into the form.
export function MemberCreatedSheet({ memberId }) {
  const { db, resendSetPasswordEmail } = useClub();
  const { closeSheet, openSheet, toast } = useUI();
  const fees = useFees();
  const navigate = useNavigate();
  const member = db.members.find((m) => m.id === memberId);

  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  if (!member) return null;

  const isPending = member.status === 'pending';
  // Life tiers never carry a duesPaidTill, so the payment book is the test.
  const paid =
    Boolean(member.duesPaidTill) || db.payments.some((p) => p.memberId === member.id);

  async function resend() {
    setWorking(true);
    setError(null);
    try {
      await resendSetPasswordEmail(member.id);
      toast(`Email sent to ${member.email}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the email.');
    }
    setWorking(false);
  }

  return (
    <>
      <SheetHead
        title={isPending ? 'Member added' : 'Membership active'}
        sub={`Bib ${bibNo(member.bib)} · ${member.name}`}
      />

      {isPending ? (
        <div className="mt-3 border border-indigo-200 bg-indigo-50/60 px-3 py-2.5">
          <p className="font-market text-[11px] leading-relaxed text-indigo-900">
            Saved to <b>Membership requests</b> — not the members list yet.{' '}
            {TIERS[member.tier].label} membership, {formatMoney(feeFor(fees, member.tier, 'join'))}{' '}
            due to join.
            {paid
              ? ' The payment is in: activate the membership from Requests.'
              : ' Once the payment is in, activate the membership from Requests — only then can they sign in.'}
          </p>
        </div>
      ) : (
        <div className="mt-3 border border-sea-300 bg-sea-50/60 px-3 py-2.5">
          <p className="font-market text-[11px] leading-relaxed text-sea-600">
            On the roster and able to sign in. {TIERS[member.tier].label} membership.
          </p>
        </div>
      )}

      {/* ---- sign-in details ---- */}
      <div className="mt-4">
        <p className="font-market text-[10.5px] uppercase tracking-wide text-copy-label">
          Sign-in details
        </p>
        <div className="mt-1.5 border border-line px-3">
          <CopyRow label="Email" value={member.email} />
          <CopyRow label="Mobile" value={member.phone} />
        </div>

        {isPending ? (
          <Hint>
            They sign in with <b>either</b> the email or the mobile number — there is no separate
            username. Nothing to send now: when you <b>activate</b> the membership, they are
            emailed a link to set their own password.
          </Hint>
        ) : member.hasPassword ? (
          <Hint>
            They have a password and sign in with the email or mobile number above. If they
            forget it, <b>Forgot password</b> on the sign-in screen sends them a reset link —
            nothing for you to do.
          </Hint>
        ) : (
          <>
            <div className="mt-3 border border-sea-300 bg-sea-50 px-3.5 py-3 font-market text-[12.5px] leading-relaxed text-sea-600">
              We have emailed <b>{member.email}</b> a link to set their password. It works once
              and is valid for 10 days; after that they sign in with the email or mobile number.
            </div>
            <div className="mt-2">
              <Button small onClick={() => void resend()} disabled={working}>
                {working ? 'Sending…' : 'Resend the email'}
              </Button>
            </div>
          </>
        )}
        <FormError>{error}</FormError>
      </div>

      <SheetActions>
        {/* "Done" used to close onto the members list — where a `pending`
            member is deliberately invisible, so the person just created was
            nowhere on screen. It goes to the queue that holds them. */}
        <Button
          onClick={() => {
            closeSheet();
            // Land on THIS person, at the top and highlighted (owner, 2 Oct 2026).
            if (isPending) navigate(`/a/requests?highlight=${encodeURIComponent(member.id)}`);
          }}
        >
          {isPending ? 'Open requests' : 'Done'}
        </Button>
        {/* Cash as well as the link: the committee often takes it in hand. */}
        {isPending && !paid && (
          <Button onClick={() => openSheet(<RecordPaymentSheet member={member} />)}>
            Record cash
          </Button>
        )}
        {isPending && !paid && (
          <Button tone="p" onClick={() => openSheet(<PayLinkSheet member={member} />)}>
            Send payment link
          </Button>
        )}
      </SheetActions>
    </>
  );
}
